import React from 'react';
import { Container, Typography, Box, Button } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

const Roadmap2026: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  return (
    <Box sx={{ minHeight: '100vh' }}>
      <Container maxWidth="md" sx={{ py: 8 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/blog')}
          sx={{ mb: 4, color: 'text.secondary' }}
        >
          {t('blog.backToBlog')}
        </Button>

        <Typography variant="overline" color="primary" sx={{ fontWeight: 600, letterSpacing: 2, display: 'block', mb: 2 }}>
          Platform News • August 11, 2026
        </Typography>

        <Typography variant="h3" component="h1" sx={{ mb: 3, fontWeight: 700 }}>
          IndeeClips 2026 Roadmap: What's Coming Next
        </Typography>

        <Typography variant="body1" color="text.secondary" sx={{ mb: 4, lineHeight: 1.85, fontSize: '1.1rem' }}>
          Transparency matters. Instead of dropping features as surprises, we're sharing our full 2026 roadmap so creators and viewers know what's coming, when to expect it, and why we're building it. Here's everything launching on IndeeClips in the next 6 months—organized by quarter, with honest timelines and feature details.
        </Typography>

        <Typography variant="h5" sx={{ mt: 6, mb: 2, fontWeight: 700 }}>
          Q3 2026 (August - September)
        </Typography>

        <Typography variant="h6" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
          Live Streaming (Beta Launch - September 2026)
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
          The most requested feature arrives this fall. Live streaming lets creators interact with audiences in real-time.
        </Typography>
        <Box component="ul" sx={{ '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li><strong>Who gets access:</strong> Channels with 500+ subscribers initially (lowering to 100+ by year-end)</li>
          <li><strong>Features:</strong> Real-time chat, stream scheduling, automatic VOD archiving, basic moderation tools</li>
          <li><strong>Monetization:</strong> Super Chat-style tipping available from day one (IndeeClips takes 20%, you keep 80%)</li>
          <li><strong>Technical specs:</strong> Up to 1080p @ 60fps, RTMP ingest for professional setups</li>
        </Box>

        <Typography variant="h6" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
          Enhanced Creator Dashboard (August 2026)
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
          Analytics get a major upgrade with actionable insights, not just raw numbers.
        </Typography>
        <Box component="ul" sx={{ '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li>Audience retention graphs—see exactly when viewers drop off</li>
          <li>Traffic source breakdown—discover how viewers find your content</li>
          <li>Best posting times—AI recommendations based on your audience activity</li>
          <li>Competitor benchmarking—compare your growth to similar channels (anonymized data)</li>
        </Box>

        <Typography variant="h5" sx={{ mt: 6, mb: 2, fontWeight: 700 }}>
          Q4 2026 (October - December)
        </Typography>

        <Typography variant="h6" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
          Creator Monetization Program (November 2026)
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
          The big one. IndeeClips launches its official partner program with creator-friendly requirements.
        </Typography>
        <Box component="ul" sx={{ '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li><strong>Eligibility:</strong> 1,000 subscribers + 10,000 video views in last 90 days (much lower than YouTube's 4,000 watch hours)</li>
          <li><strong>Revenue share:</strong> 60% to creators, 40% to platform (vs YouTube's 55/45 split)</li>
          <li><strong>Payment threshold:</strong> ₹1,000 minimum payout (vs YouTube's ₹8,000)</li>
          <li><strong>Payment schedule:</strong> Weekly payouts, not monthly</li>
          <li><strong>Ad formats:</strong> Pre-roll, mid-roll (opt-in only), and display ads</li>
        </Box>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3, lineHeight: 1.85 }}>
          Application opens October 1st. We'll onboard creators in batches to ensure quality support during ramp-up.
        </Typography>

        <Typography variant="h6" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
          Multi-Language Interface (December 2026)
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
          IndeeClips goes beyond English and Hindi. Full platform localization for 6 new languages:
        </Typography>
        <Box component="ul" sx={{ '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li>Tamil, Telugu, Marathi, Gujarati, Kannada, Bengali</li>
          <li>UI elements, menus, notifications—everything translated</li>
          <li>Regional language content discovery improved with native search</li>
        </Box>

        <Typography variant="h6" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
          Offline Download Feature (December 2026)
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
          Watch without internet—critical for users in areas with unreliable connectivity.
        </Typography>
        <Box component="ul" sx={{ '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li>Download up to 25 videos for offline viewing</li>
          <li>Videos expire after 48 hours (to prevent piracy)</li>
          <li>Quality options: 480p, 720p (conserves storage space)</li>
          <li>Creators can disable downloads on specific videos if needed</li>
        </Box>

        <Typography variant="h5" sx={{ mt: 6, mb: 2, fontWeight: 700 }}>
          Q1 2027 Preview (What's Next)
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
          Looking ahead to early 2027, here's what's in development:
        </Typography>
        <Box component="ul" sx={{ '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li><strong>Community posts:</strong> Text/image updates without uploading videos (like YouTube Community tab)</li>
          <li><strong>Collaborative playlists:</strong> Let viewers contribute to playlists you create</li>
          <li><strong>AI-powered dubbing:</strong> Upload in Hindi, auto-generate Tamil/Telugu versions with your voice</li>
          <li><strong>Channel memberships:</strong> Paid subscription tiers with exclusive perks (badges, emojis, early access)</li>
        </Box>

        <Typography variant="h5" sx={{ mt: 6, mb: 2, fontWeight: 700 }}>
          Features We're NOT Building (And Why)
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
          Transparency also means saying no. Here are popular requests we're intentionally skipping:
        </Typography>
        <Box component="ul" sx={{ '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li><strong>Stories feature:</strong> Instagram/Snapchat stories don't fit our long-form + clips model—Clips already serve this need</li>
          <li><strong>DMs between users:</strong> Safety risk, moderation nightmare—we're a video platform, not a messaging app</li>
          <li><strong>NFTs/crypto integration:</strong> Volatile, regulatory uncertainty in India, not creator-focused</li>
          <li><strong>Auto-play homepage videos:</strong> Annoying UX, wastes data—videos play only when you click</li>
        </Box>

        <Typography variant="h5" sx={{ mt: 6, mb: 2, fontWeight: 700 }}>
          How We Prioritize Features
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
          Our roadmap isn't random. We prioritize based on:
        </Typography>
        <Box component="ol" sx={{ '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li><strong>Creator feedback:</strong> Monthly surveys, direct emails, creator advisory board input</li>
          <li><strong>User demand:</strong> Support tickets, feature requests, social media mentions</li>
          <li><strong>Competitive gaps:</strong> What do YouTube/Instagram have that we're missing?</li>
          <li><strong>India-specific needs:</strong> Features that solve uniquely Indian problems (offline viewing, regional languages)</li>
          <li><strong>Technical feasibility:</strong> Can we build it reliably with our team and infrastructure?</li>
        </Box>

        <Typography variant="h5" sx={{ mt: 6, mb: 2, fontWeight: 700 }}>
          How You Can Influence the Roadmap
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
          This roadmap isn't set in stone. We adjust based on feedback.
        </Typography>
        <Box component="ul" sx={{ '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li><strong>Join creator surveys:</strong> Monthly email surveys sent to active uploaders—your input shapes priorities</li>
          <li><strong>Email feature requests:</strong> contact@indeeclips.com—we read every suggestion</li>
          <li><strong>Vote on feature polls:</strong> Follow @IndeeClips on Twitter/Instagram for polls on upcoming features</li>
          <li><strong>Join beta testing:</strong> Early access to features in exchange for feedback (sign up via your creator dashboard)</li>
        </Box>

        <Typography variant="h5" sx={{ mt: 6, mb: 2, fontWeight: 700 }}>
          Realistic Timelines
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
          Dates may shift—software development is unpredictable. What we promise:
        </Typography>
        <Box component="ul" sx={{ '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li><strong>Transparency on delays:</strong> If a feature slips, we'll announce it publicly and explain why</li>
          <li><strong>No surprise removals:</strong> Announced features won't be quietly canceled—if we can't deliver, we'll say so</li>
          <li><strong>Beta access:</strong> Features often launch in beta first—you can try them early even if full release is delayed</li>
        </Box>

        <Box
          sx={{
            mt: 6,
            p: 4,
            bgcolor: 'background.paper',
            borderRadius: 2,
            border: 1,
            borderColor: 'divider',
          }}
        >
          <Typography variant="h5" sx={{ mb: 2, fontWeight: 700 }}>
            Build the Future with Us
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3, lineHeight: 1.85 }}>
            This roadmap exists because creators asked for transparency. We're building IndeeClips WITH you, not FOR you. Every feature ships because someone requested it, needed it, or voted for it. If there's something missing from this roadmap that would transform your channel—tell us.
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.85 }}>
            Suggest a feature or join beta testing:{' '}
            <Typography
              component="a"
              href="mailto:contact@indeeclips.com"
              sx={{ color: 'primary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
            >
              contact@indeeclips.com
            </Typography>
            .
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default Roadmap2026;
